/* global define */
/**
 * The model for the LSCloud /Account/Sale/:id REST endpoint
 *
 * Copyright 2014 LightSpeed Retail. All rights reserved.
 * Created by kurtbergeron on 2014-04-17.
 */
import RetailModel from 'app/src/data/models/RetailModel';
import Customer from 'app/src/data/models/Customer';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import Localize from 'app/src/localize/Localize';
import URLHelper from 'app/src/data/models/utils/URLHelper';
import Format from 'app/src/data/Format';
import PaymentServiceAlert from 'app/src/controllers/payments/PaymentServiceAlert';
import { handleCFDSaleUpdate } from 'pages/views/devices/utils/sale-handler';
import $ from 'jquery';

export default class Sale extends RetailModel {
  // Server uses the saleID field as the model's unique identifier. This
  // informs the Backbone.Model of this fact.
  get idAttribute() {
    return 'saleID';
  }

  get defaults() {
    return {
      timeStamp: '',
      discountPercent: Format.parseDecimal('0.0'),
      completed: false,
      archived: false,
      voided: false,
      referenceNumber: '',
      referenceNumberSource: '',
      tax1Rate: Format.parseDecimal('0.0'),
      tax2Rate: Format.parseDecimal('0.0'),
      change: Format.parseDecimal('0.0'),
      ticketNumber: '',
      calcDiscount: Format.parseDecimal('0.0'),
      calcTotal: Format.parseDecimal('0.0'),
      calcSubtotal: Format.parseDecimal('0.0'),
      calcTaxable: Format.parseDecimal('0.0'),
      calcNonTaxable: Format.parseDecimal('0.0'),
      calcAvgCost: Format.parseDecimal('0.0'),
      calcFIFOCost: Format.parseDecimal('0.0'),
      calcTax1: Format.parseDecimal('0.0'),
      calcTax2: Format.parseDecimal('0.0'),
      customerID: 0,
      discountID: 0,
      employeeID: 0,
      quoteID: 0,
      registerID: 0,
      shipToID: 0,
      shopID: 0,
      taxCategoryID: 0,
      taxTotal: Format.parseDecimal('0.0'),
      totalDue: Format.parseDecimal('0.0'),
      receiptPreference: '',
      displayableTotal: Format.parseDecimal('0.0'),
    };
  }

  get retailResponseName() {
    return 'Sale';
  }

  /**
   * Return the url for the Sale endpoint
   */
  url() {
    let URL = URLHelper.getAccountURL('/Sale');
    if (!this.isNew()) {
      URL += `/${this.id}`;
    }
    return `${URL}.json`;
  }

  /**
   * Overridden to parse out the superfluous json that the LSCloud returns us
   * @param response - the json object returned from the fetch call before this object has initialized from it
   * @returns {*} the json object that this object will initialize itself from
   */
  parse(response) {
    // trim out wonky stuff from the response which is present if we fetch a single model
    // but not if it's coming from a collection
    let sale = super.parse(response);

    // TODO: This is a workaround until single-item lists are returned as JSON arrays by the Cloud API
    if (sale.SaleLines) {
      if (
        _.isObject(sale.SaleLines.SaleLine) &&
        !_.isArray(sale.SaleLines.SaleLine) &&
        !_.isEmpty(sale.SaleLines.SaleLine)
      ) {
        sale.SaleLines.SaleLine = [sale.SaleLines.SaleLine];
      }
    }
    if (sale.TaxClassTotals) {
      if (
        _.isObject(sale.TaxClassTotals.Tax) &&
        !_.isArray(sale.TaxClassTotals.Tax) &&
        !_.isEmpty(sale.TaxClassTotals.Tax)
      ) {
        sale.TaxClassTotals.Tax = [sale.TaxClassTotals.Tax];
      }
    }

    // the Cloud API has a tendency to send all values as strings so let's make sure they're of the proper type
    sale.saleID = Format.parseInt(sale.saleID);
    sale.discountPercent = Format.parseDecimal(sale.discountPercent);
    sale.completed = Format.parseBoolean(sale.completed);
    sale.archived = Format.parseBoolean(sale.archived);
    sale.voided = Format.parseBoolean(sale.voided);
    sale.tax1Rate = Format.parseDecimal(sale.tax1Rate);
    sale.tax2Rate = Format.parseDecimal(sale.tax2Rate);
    sale.change = Format.parseDecimal(sale.change);
    sale.calcDiscount = Format.parseDecimal(sale.calcDiscount);
    sale.calcTotal = Format.parseDecimal(sale.calcTotal);
    sale.calcSubtotal = Format.parseDecimal(sale.calcSubtotal);
    sale.calcTaxable = Format.parseDecimal(sale.calcTaxable);
    sale.calcNonTaxable = Format.parseDecimal(sale.calcNonTaxable);
    sale.calcAvgCost = Format.parseDecimal(sale.calcAvgCost);
    sale.calcFIFOCost = Format.parseDecimal(sale.calcFIFOCost);
    sale.calcTax1 = Format.parseDecimal(sale.calcTax1);
    sale.calcTax2 = Format.parseDecimal(sale.calcTax2);
    sale.customerID = Format.parseInt(sale.customerID);
    sale.discountID = Format.parseInt(sale.discountID);
    sale.employeeID = Format.parseInt(sale.employeeID);
    sale.quoteID = Format.parseInt(sale.quoteID);
    sale.registerID = Format.parseInt(sale.registerID);
    sale.shipToID = Format.parseInt(sale.shipToID);
    sale.shopID = Format.parseInt(sale.shopID);
    sale.taxCategoryID = Format.parseInt(sale.taxCategoryID);
    sale.taxTotal = Format.parseDecimal(sale.taxTotal);
    sale.totalDue = Format.parseDecimal(sale.totalDue);
    sale.displayableTotal = Format.parseDecimal(sale.displayableTotal);
    // Only add those default if the module is enabled
    if (window.lsCloud.Modules.hasModule('register.sales.tip')) {
      sale.calcTips = Format.parseDecimal(sale.calcTips, 0);
      sale.tipEnabled = Format.parseBoolean(sale.tipEnabled, false);
      sale.tipEmployeeID = Format.parseInt(sale.tipEmployeeID);
    }
    handleCFDSaleUpdate(sale);
    return sale;
  }

  /**
   * Retrieve the Customer model from the Sale.
   *
   * @returns {Customer}   The attached customer for this sale, if any.
   */
  getCustomer() {
    return this.getRelation('Customer', Customer);
  }

  /**
   * Starts a refund for the sale.  This must be called after the sale ID has been specified.
   *
   * @returns {jQuery.Deferred.promise}   A jquery promise.  Rejected if there are any issues creating
   *                                      a listener for this register.
   */
  refund(filter) {
    var deferred = $.Deferred();

    if (!this.isNew()) {
      $.ajax(`${URLHelper.getAccountURL('/Sale')}/${this.id}/Refund.json`, {
        type: 'POST',
        dataType: 'json',
        data: filter.getQueryParameters(),
      })
        .done(
          _.bind(() => {
            deferred.resolve();
          }, this)
        )
        .fail(
          _.bind(function(jqXHR, textStatus, errorThrown) {
            console.error(
              'Failed to create a refund for the sale with the ID:',
              this.id,
              textStatus,
              errorThrown
            );
            deferred.reject();
          }, this)
        );
    } else {
      console.error('Cannot create a refund for a Sale that has not been created.');
      deferred.reject();
    }
    return deferred.promise();
  }

  /**
   * Starts a manual Payment for the sale. Calling the setupManualCC API which will deal with the gateway to retrieve
   * a TransportKey which we then use to redirect the user to a proper form page of the Gateway.
   *
   * @param {Object}  manualPaymentParameters                    - The payload that will be sent to the API
   * @param {Boolean} manualPaymentParameters.creditCardPresent  - Whether the CreditCard is Present or not.
   * @param {*}       manualPaymentParameters.balance            - Amount to be Charged on the Card
   * @param {String}  manualPaymentParameters.gateway            - The name of the Active Gateway
   *                                                               used for Manual Payment
   * @param {Object}  manualPaymentParameters.billing            - Customer's Billing Information (optional)
   * @param {Object}  manualPaymentParameters.shipping           - Customer's Shipping Information (optional)
   *
   * @returns {*}
   */
  initiateManualEntrySale(manualPaymentParameters) {
    let deferred = $.Deferred();

    $.ajax(`${URLHelper.getAccountURL('/Sale')}/${manualPaymentParameters.saleID}/setupManualCC`, {
      type: 'POST',
      contentType: 'application/json',
      dataType: 'json',
      data: JSON.stringify({
        creditCardPresent: manualPaymentParameters.creditCardPresent,
        amount: manualPaymentParameters.balance,
        processUrl: '/register.php?form_name=hosted_payment_return',
        BillingCustomer: manualPaymentParameters.billing,
        ShipToCustomer: manualPaymentParameters.shipping,
      }),
    })
      .done(response => {
        deferred.resolve(response);
      })
      .fail(jqXHR => {
        // We get a 500 from the provider if we try to send a Zip Code entry
        // that does not conform to the US ZIP.
        if (jqXHR && jqXHR.status === 500) {
          let responseXML = $.parseXML(jqXHR.responseText);
          let errorMessage = $(responseXML)
            .find('message')
            .text();
          flashNotifier('warning', errorMessage, 15);
        } else if (jqXHR && jqXHR.status === 400) {
          // A 400 Error on a Manual Entry call should nearly always be due to invalid Gateway configs.
          // Even if it isn't, this alerts the user that something went wrong
          PaymentServiceAlert.alert(
            'Invalid Gateway Credentials. Please check your Gateway Configuration and try again.'
          );
        }
        deferred.reject();
      });

    return deferred.promise();
  }
}
